// Genealogy dates are fuzzy. We store one canonical string per date, in GEDCOM date
// syntax ("3 MAR 1850", "ABT 1850", "BET 1850 AND 1860", "(phrase)"), because that
// round-trips through GEDCOM untouched. Users type American-style dates and see
// American-style output; this module converts between the two.

export const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface SimpleDate {
  year: number;
  month?: number; // 1-12
  day?: number;
  /** Original year text when it was a dual date such as "1750/51". */
  yearText?: string;
  bce?: boolean;
  /** GEDCOM calendar escape such as "@#DJULIAN@", kept verbatim. */
  calendar?: string;
}

export type DateQualifier = 'exact' | 'about' | 'calculated' | 'estimated' | 'before' | 'after' | 'between' | 'from' | 'to' | 'from-to';

export interface ParsedDate {
  qualifier: DateQualifier;
  start?: SimpleDate;
  end?: SimpleDate;
  phrase?: string;
}

function monthIndex(word: string): number | undefined {
  const w = word.toLowerCase().replace(/\.$/, '');
  if (w.length < 3) return undefined;
  for (let i = 0; i < 12; i++) {
    if (MONTH_LONG[i].toLowerCase().startsWith(w) || (w === 'sept' && i === 8)) return i + 1;
  }
  return undefined;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function validDay(d: SimpleDate): boolean {
  if (d.month !== undefined && (d.month < 1 || d.month > 12)) return false;
  if (d.day !== undefined) {
    if (d.month === undefined) return false;
    if (d.day < 1 || d.day > daysInMonth(d.year, d.month)) return false;
  }
  return true;
}

// ---------- GEDCOM canonical parsing ----------

function parseGedcomSimple(text: string): SimpleDate | undefined {
  let t = text.trim();
  let calendar: string | undefined;
  const cal = t.match(/^(@#D[A-Z ]+@)\s*/);
  if (cal) {
    calendar = cal[1];
    t = t.slice(cal[0].length);
  }
  // GEDCOM 7 calendars are bare words (JULIAN, HEBREW, FRENCH_R, GREGORIAN).
  const cal7 = t.match(/^(GREGORIAN|JULIAN|HEBREW|FRENCH_R)\s+/);
  if (cal7) {
    calendar = cal7[1];
    t = t.slice(cal7[0].length);
  }
  let bce = false;
  const era = t.match(/\s*(B\.C\.|BCE|BC)$/i);
  if (era) {
    bce = true;
    t = t.slice(0, era.index).trim();
  }
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length === 0 || parts.length > 3) return undefined;
  const yearPart = parts[parts.length - 1];
  // A dual year ("1750/51") has a year of three or four digits, so "3/14" (a month and day) isn't one.
  const ym = yearPart.match(/^(\d{1,4})$/) ?? yearPart.match(/^(\d{3,4})\/(\d{1,2})$/);
  if (!ym) return undefined;
  const d: SimpleDate = { year: Number(ym[1]) };
  if (ym[2]) d.yearText = yearPart;
  if (bce) d.bce = true;
  if (calendar) d.calendar = calendar;
  if (parts.length >= 2) {
    const m = MONTHS.indexOf(parts[parts.length - 2].toUpperCase());
    // Non-Gregorian month codes (Hebrew TSH, French VEND…) aren't modeled; keep year only.
    if (m >= 0 && !calendar?.match(/HEBREW|FRENCH/)) d.month = m + 1;
    else if (!calendar) return undefined;
  }
  if (parts.length === 3 && d.month) {
    const day = Number(parts[0]);
    if (!Number.isInteger(day)) return undefined;
    d.day = day;
  }
  return calendar ? d : validDay(d) ? d : undefined;
}

/** Parse a canonical (GEDCOM-syntax) date string. */
export function parseGedcomDate(raw: string | undefined): ParsedDate | undefined {
  if (!raw) return undefined;
  const text = raw.trim();
  if (!text) return undefined;
  const phraseOnly = text.match(/^\((.*)\)$/);
  if (phraseOnly) return { qualifier: 'exact', phrase: phraseOnly[1] };
  // INT date with phrase: "INT 1850 (about the time of the war)"
  const intMatch = text.match(/^INT\s+(.+?)\s*\((.*)\)$/i);
  if (intMatch) {
    const start = parseGedcomSimple(intMatch[1]);
    return start ? { qualifier: 'about', start, phrase: intMatch[2] } : { qualifier: 'exact', phrase: text };
  }
  const up = text.toUpperCase();
  let m: RegExpMatchArray | null;
  if ((m = up.match(/^BET\s+(.+)\s+AND\s+(.+)$/))) {
    const start = parseGedcomSimple(m[1]);
    const end = parseGedcomSimple(m[2]);
    if (start && end) return { qualifier: 'between', start, end };
  } else if ((m = up.match(/^FROM\s+(.+)\s+TO\s+(.+)$/))) {
    const start = parseGedcomSimple(m[1]);
    const end = parseGedcomSimple(m[2]);
    if (start && end) return { qualifier: 'from-to', start, end };
  } else if ((m = up.match(/^(ABT|CAL|EST|BEF|AFT|FROM|TO)\s+(.+)$/))) {
    const q = ({ ABT: 'about', CAL: 'calculated', EST: 'estimated', BEF: 'before', AFT: 'after', FROM: 'from', TO: 'to' } as const)[
      m[1] as 'ABT'
    ];
    const start = parseGedcomSimple(m[2]);
    if (start) return { qualifier: q, start };
  } else {
    const start = parseGedcomSimple(up);
    if (start) return { qualifier: 'exact', start };
  }
  return { qualifier: 'exact', phrase: text };
}

// ---------- Canonical serialization ----------

function simpleToGedcom(d: SimpleDate): string {
  const parts: string[] = [];
  if (d.calendar) parts.push(d.calendar);
  if (d.day) parts.push(String(d.day));
  if (d.month) parts.push(MONTHS[d.month - 1]);
  parts.push(d.yearText ?? String(d.year));
  let s = parts.join(' ');
  if (d.bce) s += ' BCE';
  return s;
}

export function toGedcomDate(p: ParsedDate): string {
  if (!p.start) return p.phrase ? `(${p.phrase})` : '';
  const s = simpleToGedcom(p.start);
  switch (p.qualifier) {
    case 'exact': return s;
    case 'about': return `ABT ${s}`;
    case 'calculated': return `CAL ${s}`;
    case 'estimated': return `EST ${s}`;
    case 'before': return `BEF ${s}`;
    case 'after': return `AFT ${s}`;
    case 'from': return `FROM ${s}`;
    case 'to': return `TO ${s}`;
    case 'between': return `BET ${s} AND ${simpleToGedcom(p.end!)}`;
    case 'from-to': return `FROM ${s} TO ${simpleToGedcom(p.end!)}`;
  }
}

// ---------- American-English user input ----------

/**
 * Parse a single date as typed by an American user. Slash dates are M/D/Y.
 * Accepts: 1850 · Mar 1850 · March 3, 1850 · 3 Mar 1850 · 3/14/1850 · 1850-03-14 · 3/1850
 */
function parseUserSimple(text: string): SimpleDate | undefined {
  const t = text.trim().replace(/,/g, ' ').replace(/\s+/g, ' ');
  if (!t) return undefined;
  let m: RegExpMatchArray | null;
  let bce = false;
  let s = t;
  const era = s.match(/\s*(b\.?c\.?e?\.?|bce)$/i);
  if (era) {
    bce = true;
    s = s.slice(0, era.index).trim();
  }
  const finish = (d: SimpleDate) => {
    if (bce) d.bce = true;
    return validDay(d) ? d : undefined;
  };
  if ((m = s.match(/^(\d{1,4})$/))) return finish({ year: Number(m[1]) });
  if ((m = s.match(/^(\d{3,4})\/(\d{2})$/))) return finish({ year: Number(m[1]), yearText: s });
  // ISO: 1850-03-14 or 1850-03
  if ((m = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/)))
    return finish({ year: Number(m[1]), month: Number(m[2]), day: m[3] ? Number(m[3]) : undefined });
  // US numeric: 3/14/1850 or 3-14-1850 (month first — American convention)
  if ((m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)))
    return finish({ year: Number(m[3]), month: Number(m[1]), day: Number(m[2]) });
  if ((m = s.match(/^(\d{1,2})\/(\d{4})$/))) return finish({ year: Number(m[2]), month: Number(m[1]) });
  // Month name forms. The year may be a dual year ("1750/51"), as dates before 1752 often are.
  const words = s.split(' ');
  const yearOf = (w: string) => {
    const ym = w.match(/^(\d{1,4})(\/\d{2})?$/);
    return ym ? { year: Number(ym[1]), ...(ym[2] ? { yearText: w } : {}) } : undefined;
  };
  if (words.length === 2) {
    const mo = monthIndex(words[0]);
    const y = yearOf(words[1]);
    if (mo && y) return finish({ ...y, month: mo });
  }
  if (words.length === 3) {
    // March 3 1850
    const y = yearOf(words[2]);
    let mo = monthIndex(words[0]);
    let day = words[1].replace(/(st|nd|rd|th)$/i, '');
    if (mo && /^\d{1,2}$/.test(day) && y) return finish({ ...y, month: mo, day: Number(day) });
    // 3 March 1850
    mo = monthIndex(words[1]);
    day = words[0].replace(/(st|nd|rd|th)$/i, '');
    if (mo && /^\d{1,2}$/.test(day) && y) return finish({ ...y, month: mo, day: Number(day) });
  }
  return undefined;
}

export interface UserDateResult {
  /** Canonical string to store ('' when input was empty). */
  value: string;
  /** False when we couldn't understand the input and stored it as a free-text phrase. */
  understood: boolean;
}

const CALENDARS: Record<string, string> = { julian: '@#DJULIAN@', hebrew: '@#DHEBREW@', 'french r': '@#DFRENCH R@', gregorian: '@#DGREGORIAN@' };

/** Convert free-form American-English input into a canonical date string. */
export function parseUserDate(input: string): UserDateResult {
  const text = input.trim();
  if (!text) return { value: '', understood: true };
  // A calendar as dates are shown ("January 1, 1700 (julian)") applies to every date in the input.
  const cal = text.match(/^(.*?)\s*\((julian|hebrew|french r|gregorian)\)$/i);
  if (cal) {
    const inner = parseUserDate(cal[1]);
    const escape = CALENDARS[cal[2].toLowerCase()];
    const p = inner.understood ? parseGedcomDate(inner.value) : undefined;
    if (p?.start) {
      p.start = { ...p.start, calendar: escape };
      if (p.end) p.end = { ...p.end, calendar: escape };
      return { value: toGedcomDate(p), understood: true };
    }
  }
  // Already canonical GEDCOM? Accept as-is.
  const ged = parseGedcomDate(text);
  if (ged && !ged.phrase && /^[A-Z@\d]/.test(text) && text === text.toUpperCase()) {
    return { value: toGedcomDate(ged), understood: true };
  }
  const lower = text.toLowerCase();
  let m: RegExpMatchArray | null;

  if ((m = lower.match(/^(?:between|bet\.?)\s+(.+?)\s+(?:and|&)\s+(.+)$/))) {
    const a = parseUserSimple(m[1]);
    const b = parseUserSimple(m[2]);
    if (a && b) return { value: toGedcomDate({ qualifier: 'between', start: a, end: b }), understood: true };
  }
  if ((m = lower.match(/^from\s+(.+?)\s+(?:to|until|till)\s+(.+)$/))) {
    const a = parseUserSimple(m[1]);
    const b = parseUserSimple(m[2]);
    if (a && b) return { value: toGedcomDate({ qualifier: 'from-to', start: a, end: b }), understood: true };
  }
  // Year range "1850-1860" or "1850 – 1860" (not ISO "1850-03")
  if ((m = lower.match(/^(\d{3,4})\s*[-–—]\s*(\d{3,4})$/)) && m[2].length >= 3) {
    return {
      value: toGedcomDate({ qualifier: 'between', start: { year: Number(m[1]) }, end: { year: Number(m[2]) } }),
      understood: true,
    };
  }
  // Decade "1850s"
  if ((m = lower.match(/^(\d{3})0s$/))) {
    const y = Number(m[1] + '0');
    return { value: toGedcomDate({ qualifier: 'between', start: { year: y }, end: { year: y + 9 } }), understood: true };
  }
  const prefixes: [RegExp, DateQualifier][] = [
    [/^(?:about|abt\.?|approx\.?|approximately|around|circa|ca\.?|c\.|~)\s*/, 'about'],
    [/^(?:before|bef\.?|by|<)\s*/, 'before'],
    [/^(?:after|aft\.?|>)\s*/, 'after'],
    [/^(?:estimated|est\.?)\s*/, 'estimated'],
    [/^(?:calculated|cal\.?)\s*/, 'calculated'],
    [/^(?:from|since)\s+/, 'from'],
    [/^(?:to|until)\s+/, 'to'],
  ];
  for (const [re, q] of prefixes) {
    const pm = lower.match(re);
    if (pm) {
      const d = parseUserSimple(text.slice(pm[0].length));
      if (d) return { value: toGedcomDate({ qualifier: q, start: d }), understood: true };
    }
  }
  const d = parseUserSimple(text);
  if (d) return { value: toGedcomDate({ qualifier: 'exact', start: d }), understood: true };
  return { value: `(${text.replace(/[()]/g, '')})`, understood: false };
}

// ---------- Display ----------

function simpleToDisplay(d: SimpleDate, long = false): string {
  const year = (d.yearText ?? String(d.year)) + (d.bce ? ' BCE' : '');
  const months = long ? MONTH_LONG : MONTH_SHORT;
  const cal = d.calendar && !/GREGORIAN/.test(d.calendar) ? ` (${d.calendar.replace(/[@#]/g, '').replace(/^D/, '').toLowerCase()})` : '';
  if (d.month && d.day) return `${months[d.month - 1]} ${d.day}, ${year}${cal}`;
  if (d.month) return `${months[d.month - 1]} ${year}${cal}`;
  return year + cal;
}

/** Human-readable American-English rendering of a canonical date. */
export function formatDate(raw: string | undefined, long = false): string {
  const p = parseGedcomDate(raw);
  if (!p) return '';
  if (!p.start) return p.phrase ?? '';
  const s = simpleToDisplay(p.start, long);
  switch (p.qualifier) {
    case 'exact': return s;
    case 'about': return `about ${s}`;
    case 'calculated': return `calculated ${s}`;
    case 'estimated': return `estimated ${s}`;
    case 'before': return `before ${s}`;
    case 'after': return `after ${s}`;
    case 'from': return `from ${s}`;
    case 'to': return `until ${s}`;
    case 'between': return `between ${s} and ${simpleToDisplay(p.end!, long)}`;
    case 'from-to': return `from ${s} to ${simpleToDisplay(p.end!, long)}`;
  }
}

/** Best-guess year for a canonical date, or undefined. */
export function dateYear(raw: string | undefined): number | undefined {
  const p = parseGedcomDate(raw);
  if (!p?.start) return undefined;
  const y = p.start.bce ? -p.start.year : p.start.year;
  if (p.end && (p.qualifier === 'between' || p.qualifier === 'from-to')) {
    const e = p.end.bce ? -p.end.year : p.end.year;
    return Math.round((y + e) / 2);
  }
  return y;
}

/** Numeric key for sorting chronologically. Undated values sort last. */
export function dateSortKey(raw: string | undefined): number {
  const p = parseGedcomDate(raw);
  if (!p?.start) return Number.POSITIVE_INFINITY;
  const d = p.start;
  const y = d.bce ? -d.year : d.year;
  let key = y * 10000 + (d.month ?? 0) * 100 + (d.day ?? 0);
  if (p.qualifier === 'before') key -= 0.5;
  if (p.qualifier === 'after') key += 0.5;
  return key;
}

/** Year span for range searches: [earliest, latest] plausible years. */
export function dateYearRange(raw: string | undefined): [number, number] | undefined {
  const p = parseGedcomDate(raw);
  if (!p?.start) return undefined;
  const a = p.start.bce ? -p.start.year : p.start.year;
  if (p.end) {
    const b = p.end.bce ? -p.end.year : p.end.year;
    return [Math.min(a, b), Math.max(a, b)];
  }
  if (p.qualifier === 'about' || p.qualifier === 'estimated' || p.qualifier === 'calculated') return [a - 2, a + 2];
  if (p.qualifier === 'before') return [a - 10, a];
  if (p.qualifier === 'after') return [a, a + 10];
  return [a, a];
}
